<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { Play, FileText, Eye, Calendar } from 'lucide-vue-next'
import type { ContentItem } from './ContentCard.vue'

defineProps<{
  content: ContentItem
  index?: number
}>()

function getSubjectBadgeClass(color: string | null): string {
  if (!color) return 'bg-blue-100 text-blue-700'
  const colorMap: Record<string, string> = {
    '#3B82F6': 'bg-blue-100 text-blue-700',
    '#10B981': 'bg-green-100 text-green-700',
    '#8B5CF6': 'bg-purple-100 text-purple-700',
    '#F59E0B': 'bg-orange-100 text-orange-700',
    '#14B8A6': 'bg-teal-100 text-teal-700',
    '#EC4899': 'bg-pink-100 text-pink-700',
  }
  return colorMap[color] || 'bg-blue-100 text-blue-700'
}

function getGradientClass(index: number): string {
  const gradients = [
    'from-blue-500 to-blue-600',
    'from-green-500 to-green-600',
    'from-purple-500 to-purple-600',
    'from-orange-500 to-orange-600',
    'from-teal-500 to-teal-600',
    'from-pink-500 to-pink-600',
  ]
  return gradients[index % gradients.length]
}

function getProgressBarClass(index: number): string {
  const colors = [
    'bg-blue-600',
    'bg-green-600',
    'bg-purple-600',
    'bg-orange-600',
    'bg-teal-600',
    'bg-pink-600',
  ]
  return colors[index % colors.length]
}

function getButtonClass(index: number): string {
  const colors = [
    'bg-blue-600 hover:bg-blue-700',
    'bg-green-600 hover:bg-green-700',
    'bg-purple-600 hover:bg-purple-700',
    'bg-orange-600 hover:bg-orange-700',
    'bg-teal-600 hover:bg-teal-700',
    'bg-pink-600 hover:bg-pink-700',
  ]
  return colors[index % colors.length]
}
</script>

<template>
  <Link
    :href="`/student/explore/${content.id}`"
    class="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md hover:border-gray-300 transition-all cursor-pointer block"
  >
    <div class="flex items-stretch">
      <!-- Thumbnail -->
      <div :class="['relative w-40 md:w-48 shrink-0 self-stretch bg-gradient-to-br', getGradientClass(index ?? 0)]">
        <img
          v-if="content.thumbnail_url"
          :src="content.thumbnail_url"
          :alt="content.title"
          class="absolute inset-0 w-full h-full object-cover"
        >
        <div v-else class="absolute inset-0 flex items-center justify-center">
          <Play v-if="content.type === 'video'" class="w-8 h-8 text-white opacity-80" />
          <FileText v-else class="w-8 h-8 text-white opacity-80" />
        </div>

        <!-- NEW Badge Overlay -->
        <div v-if="content.progress === 0 && !content.published_at?.includes('day')" class="absolute top-2 left-2">
          <span class="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded">NEW</span>
        </div>

        <!-- Duration Badge -->
        <div class="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
          {{ content.duration }}
        </div>

        <!-- Progress Overlay -->
        <div v-if="content.progress > 0" class="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
          <div
            :class="['h-full', getProgressBarClass(index ?? 0)]"
            :style="{ width: `${content.progress}%` }"
          ></div>
        </div>
      </div>

      <!-- Content Details -->
      <div class="flex-1 p-4 flex flex-col justify-center min-w-0">
        <!-- Title & Tags Row -->
        <div class="flex items-start justify-between gap-4 mb-2">
          <h3 class="font-semibold text-gray-900 text-base line-clamp-1 flex-1">{{ content.title }}</h3>
          <div class="flex items-center gap-1.5 shrink-0">
            <span
              v-if="content.subject"
              :class="['text-xs font-medium px-2 py-0.5 rounded', getSubjectBadgeClass(content.subject.color)]"
            >
              {{ content.subject.name }}
            </span>
            <span
              v-if="content.level"
              class="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded"
            >
              {{ content.level.name }}
            </span>
          </div>
        </div>

        <!-- Description -->
        <p v-if="content.description" class="text-sm text-gray-500 line-clamp-1 mb-3">
          {{ content.description }}
        </p>

        <!-- Meta Row with Action Button -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
            <!-- Tutor -->
            <div class="flex items-center gap-1.5">
              <img
                v-if="content.tutor.avatar_url"
                :src="content.tutor.avatar_url"
                :alt="content.tutor.name"
                class="w-5 h-5 rounded-full border border-gray-200 object-cover"
              >
              <div v-else class="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                <span class="text-xs text-blue-600 font-semibold">{{ content.tutor.name.charAt(0) }}</span>
              </div>
              <span class="font-medium text-gray-700">{{ content.tutor.name }}</span>
            </div>

            <span class="text-gray-300">|</span>

            <!-- Type -->
            <span class="capitalize">{{ content.type }}</span>

            <span class="text-gray-300">|</span>

            <!-- Views -->
            <span class="flex items-center gap-1">
              <Eye class="w-3.5 h-3.5" />
              {{ content.view_count.toLocaleString() }}
            </span>

            <span class="text-gray-300">|</span>

            <!-- Published -->
            <span class="flex items-center gap-1">
              <Calendar class="w-3.5 h-3.5" />
              {{ content.published_at }}
            </span>

            <!-- Progress Indicator -->
            <template v-if="content.progress > 0 && content.progress < 100">
              <span class="text-gray-300">|</span>
              <div class="flex items-center gap-2">
                <div class="w-16 bg-gray-200 rounded-full h-1.5">
                  <div
                    :class="['h-1.5 rounded-full', getProgressBarClass(index ?? 0)]"
                    :style="{ width: `${content.progress}%` }"
                  ></div>
                </div>
                <span class="text-xs font-medium text-gray-600">{{ content.progress }}%</span>
              </div>
            </template>
            <template v-else-if="content.progress >= 100">
              <span class="text-gray-300">|</span>
              <span class="text-xs font-semibold text-green-600 flex items-center gap-1">
                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Completed
              </span>
            </template>

            <!-- Examboard -->
            <template v-if="content.examboard">
              <span class="text-gray-300">|</span>
              <span class="bg-amber-100 text-amber-700 text-xs font-medium px-1.5 py-0.5 rounded">
                {{ content.examboard.name }}
              </span>
            </template>

            <!-- Free Badge -->
            <template v-if="content.is_free && content.progress === 0">
              <span class="text-gray-300">|</span>
              <span class="text-xs font-semibold text-green-600">Free</span>
            </template>
          </div>

          <!-- Action Button -->
          <span
            :class="['text-white px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors shrink-0 ml-4', getButtonClass(index ?? 0)]"
          >
            <template v-if="content.progress > 0 && content.progress < 100">Continue</template>
            <template v-else-if="content.progress >= 100">Watch Again</template>
            <template v-else>
              <template v-if="content.type === 'video'">Watch</template>
              <template v-else>View</template>
            </template>
          </span>
        </div>
      </div>
    </div>
  </Link>
</template>
